<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { HrDate, HrIcon, HrMenu, HrRadio } from '../primitives';
import HrActionMenu from './HrActionMenu.vue';
import HrConfirmBtn from './HrConfirmBtn.vue';
import HrDismissBtn from './HrDismissBtn.vue';
import HrFormDialog from './HrFormDialog.vue';
import HrSubtleBtn from './HrSubtleBtn.vue';

/**
 * Form dialog for an effective-dated record — shows which version is being edited, and asks when the
 * change takes effect before saving.
 *
 * Two steps in one dialog. The first is an ordinary form with a line above it naming the version in
 * view; submitting does not save but advances to the second, where the user chooses the date the new
 * version starts. Nothing is written until that choice is confirmed, which is the point: for a record
 * the rest of the system resolves by date, "when" is part of the edit rather than a detail to infer
 * from it.
 *
 * DECIDES NOTHING, and this is the whole of its contract. It does not know what a version is, read a
 * clock, or judge which dates are allowed — every option it offers, every caption, and the earliest
 * selectable day arrive as props, and the choice leaves as an event. Effective-dating rules belong to
 * whichever context owns the record; a dialog that also held them would make each new versioned
 * record a fork of this file.
 *
 * WHY THE OPTIONS ARE DATA rather than three built-in buttons. Their captions carry the caller's
 * domain values — "Fixes version 3 in place. Treated as if it was always $120.00 since Jan 1" — which
 * no generic component could compose. It also means a fourth option, or one fewer, is a change at the
 * call site. An option that carries `withDatePicker` expands to an inline calendar when chosen.
 *
 * @status recommended
 * @group cards-dialogs
 * @useWhen Editing a record that is versioned by effective date, where saving has to ask from when
 * @avoidWhen An ordinary form with a single save action — use HrFormDialog
 * @avoidWhen The record has no effective date and no history — the "when" step would ask nothing
 * @related HrFormDialog
 * @related HrAdaptiveFormDialog
 * @a11y The step buttons are ordinary buttons and the options a radio group, so the whole flow is
 *   keyboard-reachable. Advancing runs the form's own validation first, so errors are announced on
 *   the step that owns the fields rather than after the user has left it.
 * @interfaceShape imperative
 */
defineOptions({ name: 'HrVersionedFormDialog' });

/**
 * @typedef {{ value: *, label: string }} HrVersionedFormDialogVersion
 * @typedef {{ value: *, label: string, caption?: string, disabled?: boolean,
 *   withDatePicker?: boolean }} HrVersionedFormDialogSaveOption
 */

const props = defineProps({
  /** Dialog title while the form is shown. */
  title: {
    type: String,
    required: true,
  },
  /**
   * Dialog title on the "when does this take effect" step. Supplied rather than built in so the
   * question can name what is being dated in the caller's own words.
   * @example When does this take effect?
   */
  whenTitle: {
    type: String,
    required: true,
  },
  /**
   * Dialog width preset.
   * @values sm, md, lg
   */
  size: {
    type: String,
    default: 'sm',
    validator: (v) => ['sm', 'md', 'lg'].includes(v),
  },
  /**
   * The version in view, as a complete phrase — e.g. "Valid from Jan 1, 2026". Null hides the line.
   *
   * Formatted by the caller, deliberately. A date's display form is a locale and product decision,
   * and this component holds neither the raw value nor the vocabulary to introduce one.
   *
   * NULL IS AN ORDINARY STATE, not a caller mistake, which is why this is optional. A record being
   * CREATED has no version to name, and a record being loaded has none yet either — the series
   * arrives asynchronously, so the first render of an edit is indistinguishable from a create. A
   * required prop would make both of those a console error on every open.
   * @example Valid from Jan 1, 2026
   */
  effectiveFromLabel: {
    type: String,
    default: null,
  },
  /**
   * Short version marker shown beside the date — e.g. "v3". Null hides it, which is what a caller
   * does for the only version on a date, where "v1" tells the user nothing.
   */
  versionLabel: {
    type: String,
    default: null,
  },
  /**
   * Other versions the user may switch to, as `{ value, label }`. Empty leaves the date line as plain
   * text; a non-empty list makes it a menu. Informational either way — switching is the caller's to
   * carry out, since it reloads the form.
   * @type {HrVersionedFormDialogVersion[]}
   * @example [{"value":"2026-09-01","label":"Sep 1, 2026 — scheduled"}]
   */
  versionOptions: {
    type: Array,
    default: () => [],
  },
  /**
   * The dates a new version may start, as `{ value, label, caption, disabled, withDatePicker }`.
   * `withDatePicker` expands the option to an inline calendar when chosen; at most one option should
   * carry it.
   * @type {HrVersionedFormDialogSaveOption[]}
   * @example [
   *   {"value":"amend","label":"Correct the current version",
   *    "caption":"Fixes version 3 in place — no new period."},
   *   {"value":"today","label":"Today — Feb 4, 2026",
   *    "caption":"The previous version stands through yesterday."},
   *   {"value":"on-date","label":"On a specific date",
   *    "caption":"Pick the day the new version starts.","withDatePicker":true}
   * ]
   */
  saveOptions: {
    type: Array,
    default: () => [],
  },
  /** The chosen option's value. Two-way bindable via `v-model:option`. */
  option: {
    type: null,
    default: null,
  },
  /**
   * The chosen calendar date as 'YYYY-MM-DD', for the option that expands a picker. Two-way bindable
   * via `v-model:date`.
   *
   * NOT a Date and NOT a Firestore Timestamp. An effective date is a calendar date — it has no time
   * of day and no zone — and converting an instant to one needs a timezone this component cannot
   * know.
   */
  date: {
    type: String,
    default: null,
  },
  /**
   * Earliest selectable day as 'YYYY-MM-DD'; null leaves the calendar unbounded. A guardrail supplied
   * by the caller, never derived here.
   * @example 2026-01-02
   */
  minDate: {
    type: String,
    default: null,
  },
  /**
   * Latest selectable day as 'YYYY-MM-DD'; null leaves the calendar unbounded at that end. The
   * caller's guardrail, like `minDate` — a record that may be backdated but not scheduled forward
   * bounds this at today.
   * @example 2026-12-31
   */
  maxDate: {
    type: String,
    default: null,
  },
  /** Label for the button that leaves the form for the "when" step. Defaults to the shared Save. */
  saveLabel: {
    type: String,
    default: null,
  },
  /** Label for the button that abandons the edit. Defaults to the shared Cancel. */
  cancelLabel: {
    type: String,
    default: null,
  },
  /**
   * Prevents saving. Set it when the form holds nothing worth recording — an unchanged record saved
   * onto a versioned timeline appends an entry that decided nothing, and a timeline is a record of
   * decisions rather than of times somebody opened a dialog.
   *
   * Only the form step is affected; the "when" step's confirm is already governed by whether a
   * complete date has been chosen.
   */
  saveDisabled: {
    type: Boolean,
    default: false,
  },
  /**
   * The save is in flight: the confirming button shows a spinner, and every control that could start a
   * second write or abandon the first is disabled.
   *
   * Going back is disabled too, not only confirming. The step behind holds the record being written,
   * so reaching it mid-write would let the user edit a draft that is already on its way to storage and
   * then watch their own save overwrite the edit.
   *
   * Named for the action in flight, matching `loading` everywhere else in the system — and leaving
   * `pending` free for its own meaning here, should the form ever want a skeleton while the record's
   * history is still arriving. The two are different states and a caller can be in both at once.
   */
  loading: {
    type: Boolean,
    default: false,
  },
  /** Label for the button that confirms the chosen date. Defaults to the shared Continue. */
  continueLabel: {
    type: String,
    default: null,
  },
});

const emit = defineEmits({
  /** Fired when the chosen option changes (supports `v-model:option`). */
  'update:option': null,
  /** Fired when the chosen date changes (supports `v-model:date`); payload is 'YYYY-MM-DD' or null. */
  'update:date': null,
  /** Fired when another version is picked from the date menu; payload is that option's `value`. */
  'select-version': null,
  /** Fired on confirm; payload is `{ option, date }` — the caller decides what that means. */
  submit: null,
  /** Fired when the dialog is dismissed without confirming. */
  close: null,
});

const { t } = useI18n();

const formDialogRef = ref(null);
/** Which step is showing. The form is never unmounted — leaving it would discard the user's edits. */
const isChoosingDate = ref(false);

const effectiveSaveLabel = computed(() => props.saveLabel ?? t('common.buttonSave'));
const effectiveContinueLabel = computed(() => props.continueLabel ?? t('common.buttonContinue'));
// HrDismissBtn renders no text of its own when unlabeled, so the label has to be supplied rather
// than left to the button — an unlabeled cancel is an invisible one.
const effectiveCancelLabel = computed(() => props.cancelLabel ?? t('common.buttonCancel'));

const chosenOption = computed(() => {
  return props.saveOptions.find((option) => option.value === props.option) ?? null;
});

/**
 * Does the current choice need a date the user has not picked yet? Drives the confirm button, so a
 * half-made choice cannot be submitted as if it were complete.
 */
const awaitsDate = computed(() => {
  return chosenOption.value?.withDatePicker === true && props.date === null;
});

const canConfirm = computed(() => chosenOption.value !== null && !awaitsDate.value);

/**
 * Unchosen options lose their captions only while an EXPANDING option is chosen, which is the design's
 * own rule rather than a uniform one: the calendar needs the room, and collapsing rows that are not
 * competing for space would hide the comparison the step exists to offer.
 */
const isCollapsed = (option) => {
  return chosenOption.value?.withDatePicker === true && option.value !== props.option;
};

/** HrDate speaks 'YYYY/MM/DD'; the public contract here is the calendar-string form used at rest. */
const toSlashes = (date) => (typeof date === 'string' ? date.replaceAll('-', '/') : null);
const toDashes = (date) => (typeof date === 'string' ? date.replaceAll('/', '-') : null);

const minDateSlashes = computed(() => toSlashes(props.minDate));
const maxDateSlashes = computed(() => toSlashes(props.maxDate));

/**
 * Lexicographic comparison is chronological for zero-padded dates, so no parsing is needed.
 *
 * Null rather than an always-true predicate when neither bound is set: HrDate takes that as "no
 * restriction" and skips the per-day call entirely.
 */
const selectableDays = computed(() => {
  if (minDateSlashes.value === null && maxDateSlashes.value === null) {
    return null;
  }
  return (day) => {
    return (minDateSlashes.value === null || day >= minDateSlashes.value)
      && (maxDateSlashes.value === null || day <= maxDateSlashes.value);
  };
});

/**
 * Is there a date to ask about at all?
 *
 * An empty option list means there is nothing to choose — a record being created has no timeline to
 * date a change against, and its first version simply starts now. So the form's confirm button
 * submits directly rather than advancing to a step that would offer nothing.
 */
const asksForDate = computed(() => props.saveOptions.length > 0);

/**
 * Runs the form's own validation before leaving it. `validate` resolves false only on a real failure,
 * so a dialog with no validated fields advances normally.
 */
async function goToDateStep() {
  if (props.loading) {
    return;
  }
  const isValid = await formDialogRef.value?.validate(true);

  if (isValid === false) {
    return;
  }
  if (asksForDate.value) {
    isChoosingDate.value = true;
    return;
  }
  emit('submit', { option: null, date: null });
}

function confirm() {
  // Guarded as well as disabled: a double-press can land before the disabled state renders, and the
  // second one would start a second write of the same record.
  if (props.loading) {
    return;
  }
  emit('submit', { option: props.option, date: props.date });
}

function close() {
  formDialogRef.value?.hide();
  emit('close');
}

defineExpose({
  /** Opens the dialog on the form step. */
  show: () => {
    isChoosingDate.value = false;
    formDialogRef.value?.show();
  },
  /** Closes the dialog. */
  hide: () => formDialogRef.value?.hide(),
  /** Validates the form step; resolves true when it passes. */
  validate: (shouldFocus) => formDialogRef.value?.validate(shouldFocus),
  /** Returns to the form step — for a caller whose save failed and who is keeping the dialog open. */
  backToForm: () => {
    isChoosingDate.value = false;
  },
});
</script>

<template>
  <hr-form-dialog
    ref="formDialogRef"
    :title="isChoosingDate ? whenTitle : title"
    :size="size"
    @close="emit('close')"
  >
    <template #header-actions>
      <!-- @slot header-actions Extra icon buttons in the header, left of the close button. -->
      <slot name="header-actions" />
    </template>

    <!--
      Both steps stay mounted and one is hidden, rather than one being rendered at a time. The form
      holds the user's unsaved edits, and going back to change a field after seeing the dates must not
      find an empty form.
    -->
    <div v-show="!isChoosingDate">
      <div
        v-if="effectiveFromLabel !== null"
        class="hr-versioned-form-dialog__version"
      >
        <template v-if="versionOptions.length > 0">
          <button
            type="button"
            class="hr-versioned-form-dialog__version-trigger hr-label-large"
            aria-haspopup="menu"
          >
            {{ effectiveFromLabel }}
            <hr-icon
              name="expand_more"
              size="xs"
            />
            <hr-menu>
              <hr-action-menu
                :options="versionOptions"
                @select="emit('select-version', $event.value)"
              />
            </hr-menu>
          </button>
        </template>
        <span
          v-else
          class="hr-label-large"
        >{{ effectiveFromLabel }}</span>

        <span
          v-if="versionLabel !== null"
          class="hr-versioned-form-dialog__version-marker hr-label-small"
        >{{ versionLabel }}</span>
      </div>

      <!-- @slot Form fields for the record being edited. -->
      <slot />
    </div>

    <!--
      Grouped explicitly: these are separate hr-radio controls, so without the role a screen reader
      announces each in isolation and never says how many there are or that choosing one unchooses
      the rest. Named from whenTitle, which is the question they answer.
    -->
    <div
      v-show="isChoosingDate"
      role="radiogroup"
      :aria-label="whenTitle"
      class="hr-versioned-form-dialog__options"
    >
      <div
        v-for="saveOption in saveOptions"
        :key="saveOption.value"
        :class="['hr-versioned-form-dialog__option', {
          'hr-versioned-form-dialog__option--chosen': saveOption.value === option,
          'hr-versioned-form-dialog__option--collapsed': isCollapsed(saveOption),
          'hr-versioned-form-dialog__option--disabled': saveOption.disabled === true,
        }]"
      >
        <hr-radio
          :model-value="option"
          :val="saveOption.value"
          :label="saveOption.label"
          :disable="saveOption.disabled === true"
          :data-e2e="`radio-save-option-${saveOption.value}`"
          @update:model-value="emit('update:option', $event)"
        />

        <p
          v-if="saveOption.caption && !isCollapsed(saveOption)"
          class="hr-versioned-form-dialog__caption hr-body-medium"
        >
          {{ saveOption.caption }}
        </p>

        <hr-date
          v-if="saveOption.withDatePicker === true && saveOption.value === option"
          class="hr-versioned-form-dialog__calendar"
          :model-value="toSlashes(date)"
          :options="selectableDays"
          @update:model-value="emit('update:date', toDashes($event))"
        />
      </div>

      <!-- @slot warning A banner shown above the actions — e.g. that confirming replaces a scheduled version. -->
      <slot name="warning" />
    </div>

    <template #actions>
      <template v-if="isChoosingDate">
        <hr-subtle-btn
          :label="t('common.buttonBack')"
          :disable="loading"
          @click="isChoosingDate = false"
        />
        <hr-confirm-btn
          :label="effectiveContinueLabel"
          :disable="!canConfirm || loading"
          :loading="loading"
          data-e2e="button-continue"
          @click="confirm"
        />
      </template>
      <template v-else>
        <hr-dismiss-btn
          :label="effectiveCancelLabel"
          :disable="loading"
          @click="close"
        />
        <hr-confirm-btn
          :label="effectiveSaveLabel"
          :disable="saveDisabled || loading"
          :loading="loading"
          data-e2e="button-save"
          @click="goToDateStep"
        />
      </template>
    </template>
  </hr-form-dialog>
</template>

<style lang="scss" scoped>
.hr-versioned-form-dialog__version {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 16px;
  color: var(--app-on-surface-variant);
}

.hr-versioned-form-dialog__version-trigger {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.hr-versioned-form-dialog__version-marker {
  padding: 2px 8px;
  border-radius: var(--app-shape-corner-full);
  background: var(--app-surface-container-high);
  color: var(--app-on-surface-variant);
}

.hr-versioned-form-dialog__options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.hr-versioned-form-dialog__option {
  padding: 12px 16px;
  border: 1px solid var(--app-outline-variant);
  border-radius: var(--app-shape-corner-medium);
}

.hr-versioned-form-dialog__option--chosen {
  border-width: 2px;
  border-color: var(--app-on-surface);
  padding: 11px 15px;
}

.hr-versioned-form-dialog__option--collapsed {
  color: var(--app-on-surface-variant);
  opacity: 0.6;
}

.hr-versioned-form-dialog__option--disabled {
  opacity: 0.38;
}

.hr-versioned-form-dialog__caption {
  margin: 4px 0 0;
  margin-inline-start: 32px;
  color: var(--app-on-surface-variant);
}

.hr-versioned-form-dialog__calendar {
  margin-top: 12px;
}
</style>
